import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import { ViewMode } from '../contexts/ReleasesContext';

// Define route handle interface for type safety
// Note: React Router defines handle as 'any', so we use this interface for our own type checking
export interface RouteHandle {
  viewMode?: ViewMode;
}

// Core imports (needed for routing structure)
import { ChapterSnapshotProvider } from '../contexts/ChapterSnapshotContext';
import { ChapterProvider } from '../contexts/ChapterContext';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import { PlanProvider } from '../contexts/PlanContext';
import { QueueProvider } from '../contexts/QueueContext';
import { ReleasesProvider } from '../contexts/ReleasesContext';
import { AuditProvider } from '../contexts/AuditContext';
import { AppWrapper } from '../layout/AppWrapper';
import PermissionsRoute from '../layout/PermissionsRoute';
import { ProtectedRoute } from '../layout/ProtectedRoute';
import { AuditLayoutWrapper } from '../layout/AuditLayoutWrapper';
import { OnboardingLayoutWrapper } from '../layout/OnboardingLayoutWrapper';
import MinimalAppWrapper from '../layout/MinimalAppWrapper';
import { LoadingFallback } from '../components/LoadingFallback';

// Import roles eagerly (needed for route definitions)
import { ProfileRoles } from '../pages/settings/Profile';
import { OrganisationRoles } from '../pages/settings/Organisation';
import { InvoiceRoles } from '../pages/settings/Invoices';
import { NotificationRoles } from '../pages/settings/Notifications';
import { SecurityRoles } from '../pages/settings/Security';
import { SubscriptionRoles } from '../pages/settings/Subscription';
import { TeamRoles } from '../pages/settings/Team';

// Eager-loaded (frequently used)
import Dashboard from '../pages/app/Dashboard';

// Lazy-loaded: Auth pages
const Login = lazy(() => import('../pages/auth/Login'));
const Signup = lazy(() => import('../pages/auth/Signup'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const Logout = lazy(() => import('../pages/auth/Logout'));
const JoinOrganisation = lazy(() => import('../pages/auth/Join'));
const VerifyEmailPending = lazy(() => import('../pages/auth/VerifyEmailPending'));
const VerifyEmailSuccess = lazy(() => import('../pages/auth/VerifyEmailSuccess'));
const AuthCallbackPage = lazy(() => import('../pages/auth/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));
const SsoPage = lazy(() => import('../pages/auth/SsoPage').then(m => ({ default: m.SsoPage })));
const SelectOrganisation = lazy(() => import('../pages/auth/SelectOrganisation'));
const Welcome = lazy(() => import('../pages/auth/Welcome'));

// Lazy-loaded: Onboarding flow (includes Stripe)
const OnboardingWrapper = lazy(() => import('../pages/onboarding/OnboardingWrapper'));
const OnboardingOrganisation = lazy(() => import('../pages/onboarding/OnboardingOrganisation'));
const OnboardingPayment = lazy(() => import('../pages/onboarding/OnboardingPayment'));
const OnboardingSubscription = lazy(() => import('../pages/onboarding/OnboardingSubscription'));
const OnboardingWelcome = lazy(() => import('../pages/onboarding/OnboardingWelcome'));

// Lazy-loaded: Settings pages
const Settings = lazy(() => import('../pages/app/Settings'));
const Profile = lazy(() => import('../pages/settings/Profile'));
const Organisation = lazy(() => import('../pages/settings/Organisation'));
const Cards = lazy(() => import('../pages/settings/components/Cards'));
const Invoices = lazy(() => import('../pages/settings/Invoices'));
const Notifications = lazy(() => import('../pages/settings/Notifications'));
const Security = lazy(() => import('../pages/settings/Security'));
const Subscription = lazy(() => import('../pages/settings/Subscription'));
const Team = lazy(() => import('../pages/settings/Team'));

// Lazy-loaded: Audit module
const AuditsList = lazy(() => import('../pages/app/audit/AuditsList'));
const AuditCreate = lazy(() => import('../pages/app/audit/AuditCreate'));
const AuditViewer = lazy(() => import('../pages/app/audit/AuditViewer'));
const AuditReport = lazy(() => import('../pages/app/audit/AuditReport'));
const CorrectiveActionsList = lazy(() => import('../pages/app/audit/CorrectiveActionsList'));

// Lazy-loaded: Flow editor (includes reactflow)
const PlansEdit = lazy(() => import('../pages/app/plans/PlansEdit'));

// Lazy-loaded: Release pages (may include PDF/Flow rendering)
const ReleaseChapterPages = lazy(() => import('../pages/app/releases/ReleaseChapter'));
const ReleasePageContent = lazy(() => import('../pages/app/releases/ReleasePageContent'));
const ReleaseCCPContent = lazy(() => import('../pages/app/releases/ReleaseCCPContent'));
const ReleaseHazardContent = lazy(() => import('../pages/app/releases/ReleaseHazardContent'));
const ReleaseRecordCollections = lazy(() => import('../pages/app/releases/ReleaseRecordCollections'));
const ReleaseRecordsViewer = lazy(() => import('../pages/app/releases/ReleaseRecordsViewer'));
const ReleaseGoodsCollections = lazy(() => import('../pages/app/releases/ReleaseGoodsCollections'));
const ReleaseGoodsViewer = lazy(() => import('../pages/app/releases/ReleaseGoodsViewer'));
const ReleasesDetail = lazy(() => import('../pages/app/releases/ReleaseDetail'));

// Lazy-loaded: Records
const RecordCollectionsList = lazy(() => import('../pages/app/records/RecordCollectionsList'));
const RecordCollectionBuilder = lazy(() => import('../pages/app/records/RecordCollectionBuilder'));
const RecordsViewer = lazy(() => import('../pages/app/records/RecordsViewer'));
const RecordCollectorPage = lazy(() => import('../pages/app/records/RecordCollectorPage'));
const RecordPlanViewer = lazy(() => import('../pages/app/records/RecordPlanViewer'));

// Lazy-loaded: Suppliers
const SuppliersList = lazy(() => import('../pages/app/suppliers/SuppliersList'));
const SupplierViewer = lazy(() => import('../pages/app/suppliers/SupplierViewer'));

// Lazy-loaded: Incoming Goods
const GoodsList = lazy(() => import('../pages/app/goods/GoodsList'));
const GoodsBuilder = lazy(() => import('../pages/app/goods/GoodsBuilder'));
const GoodsViewer = lazy(() => import('../pages/app/goods/GoodsViewer'));
const GoodsReceiverPage = lazy(() => import('../pages/app/goods/GoodsReceiverPage'));
const GoodsPlanViewer = lazy(() => import('../pages/app/goods/GoodsPlanViewer'));

// Lazy-loaded: Other app pages
const ChaptersCreate = lazy(() => import('../pages/app/chapters/ChaptersCreate'));
const ChaptersList = lazy(() => import('../pages/app/chapters/ChaptersList'));
const HazardEditor = lazy(() => import('../pages/app/hazards/HazardEditor'));
const CCPEditor = lazy(() => import('../pages/app/ccp/CCPEditor'));
const PagesCreate = lazy(() => import('../pages/app/pages/PagesCreate'));
const PagesEdit = lazy(() => import('../pages/app/pages/PagesEdit'));
const PlansCreate = lazy(() => import('../pages/app/plans/PlansCreate'));
const PlansCreateAI = lazy(() => import('../pages/app/plans/PlansCreateAI'));
const PlanImport = lazy(() => import('../pages/app/import/PlanImport'));
const PlansList = lazy(() => import('../pages/app/plans/PlansList'));
const ReleasesCreate = lazy(() => import('../pages/app/releases/ReleaseCreate'));
const ReleasesList = lazy(() => import('../pages/app/releases/ReleasesList'));
const PagesListEntry = lazy(() => import('../pages/app/pages/PagesListEntry'));

// Lazy-loaded: Upgrade page (shown after free trial expires)
const UpgradePage = lazy(() => import('../pages/app/UpgradePage'));

// Lazy-loaded: Error pages
const Error403 = lazy(() => import('../pages/errors').then(m => ({ default: m.Error403 })));
const Error404 = lazy(() => import('../pages/errors').then(m => ({ default: m.Error404 })));
const Error500 = lazy(() => import('../pages/errors').then(m => ({ default: m.Error500 })));
const Error418 = lazy(() => import('../pages/errors/Error418'));

// Helper to wrap components with Suspense
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

// Helper to wrap components with AuditLayoutWrapper + Suspense + latest viewMode
const withAuditLayout = (Component: React.LazyExoticComponent<any>, additionalWrapper?: React.ReactElement) => ({
  element: (
    <AuditLayoutWrapper>
      {additionalWrapper || (
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      )}
    </AuditLayoutWrapper>
  ),
  handle: { viewMode: 'latest' } satisfies RouteHandle,
});

// Helper to wrap components with OnboardingLayoutWrapper + Suspense + edit viewMode
const withOnboardingLayout = (element: React.ReactElement) => ({
  element: (
    <OnboardingLayoutWrapper>
      {element}
    </OnboardingLayoutWrapper>
  ),
  handle: { viewMode: 'edit' } satisfies RouteHandle,
});

// Helper to wrap components with AuditProvider + Suspense + edit viewMode
const withAuditProvider = (Component: React.LazyExoticComponent<any>) => ({
  element: (
    <AuditProvider>
      <Suspense fallback={<LoadingFallback />}>
        <Component />
      </Suspense>
    </AuditProvider>
  ),
  handle: { viewMode: 'edit' } satisfies RouteHandle,
});

export const routes: RouteObject[] = [
  // Root redirect
  {
    path: '/',
    element: <Navigate to="/app" replace />,
  },
  // dashboard redirect
  {
    path: '/dashboard',
    element: <Navigate to="/app" replace />,
  },

  // Account/Auth routes
  {
    path: '/',
    children: [
      // Public routes
      {
        path: 'login',
        element: withSuspense(Login),
      },
      {
        path: 'signup',
        element: withSuspense(Signup),
      },
      {
        path: 'forgot-password',
        element: withSuspense(ForgotPassword),
      },
      {
        path: 'reset-password',
        element: withSuspense(ResetPassword),
      },
      {
        path: 'logout',
        element: withSuspense(Logout),
      },
      {
        path: '/join/:token',
        element: withSuspense(JoinOrganisation),
      },
      {
        path: 'verify-email-pending',
        element: withSuspense(VerifyEmailPending),
      },
      {
        path: 'verify-email/:token',
        element: withSuspense(VerifyEmailSuccess),
      },
    ],
  },
  // OAuth callback
  {
    path: '/auth/callback',
    element: withSuspense(AuthCallbackPage),
  },
  // SSO cross-domain login
  {
    path: '/auth/sso',
    element: withSuspense(SsoPage),
  },

  // Select organisation shouldn't have the app wrapper
  {
    path: '/select-organisation',
    element: (
      <ProtectedRoute>
        <PermissionsRoute requireOrganisation={false}>
          <Suspense fallback={<LoadingFallback />}>
            <SelectOrganisation />
          </Suspense>
        </PermissionsRoute>
      </ProtectedRoute>
    ),
  },

  // welcome page for new invited users
  {
    path: '/welcome',
    element: (
      <ProtectedRoute>
        <PermissionsRoute requireOrganisation={false}>
          <Suspense fallback={<LoadingFallback />}>
            <Welcome />
          </Suspense>
        </PermissionsRoute>
      </ProtectedRoute>
    ),
  },

  // Main app routes (protected)
  {
    path: '/app',
    element: (
      <ProtectedRoute>
        <NotificationsProvider>
          <PlanProvider>
            <ReleasesProvider>
              <QueueProvider>
                <AppWrapper />
              </QueueProvider>
            </ReleasesProvider>
          </PlanProvider>
        </NotificationsProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Dashboard />,
      },
      {
        path: 'plans',
        element: withSuspense(PlansList),
      },
      {
        path: 'plans/create',
        element: withSuspense(PlansCreate),
      },
      {
        path: 'plans/create-ai',
        element: withSuspense(PlansCreateAI),
      },
      {
        path: 'plans/import',
        element: withSuspense(PlanImport),
      },
      {
        path: 'plans/:planId/edit',
        element: withSuspense(PlansEdit),
      },
      // Onboarding/new plan routes - all use OnboardingLayoutWrapper with 'edit' viewMode
      {
        path: 'plans/:planId/new/chapters',
        ...withOnboardingLayout(withSuspense(ChaptersList)),
      },
      {
        path: 'plans/:planId/new/chapters/create',
        ...withOnboardingLayout(withSuspense(ChaptersCreate)),
      },
      {
        path: 'plans/:planId/new/chapters/:chapterId',
        ...withOnboardingLayout(<ChapterProvider />),
        children: [
          {
            path: 'pages',
            element: withSuspense(PagesListEntry),
          },
          {
            path: 'pages/create',
            element: withSuspense(PagesCreate),
          },
          {
            path: 'pages/:pageId/content',
            element: withSuspense(PagesEdit),
          },
          {
            path: 'pages/:pageId/edit',
            element: withSuspense(PagesEdit),
          },
          {
            path: 'hazards/:hazardId',
            element: withSuspense(HazardEditor),
          },
          {
            path: 'ccps/:ccpId',
            element: withSuspense(CCPEditor),
          },
        ],
      },
      {
        path: 'plans/:planId/records',
        ...withOnboardingLayout(withSuspense(RecordCollectionsList)),
      },
      {
        path: 'plans/:planId/records/:collectionId',
        ...withOnboardingLayout(withSuspense(RecordCollectionBuilder)),
      },
      {
        path: 'plans/:planId/records/:collectionId/entries',
        ...withOnboardingLayout(withSuspense(RecordsViewer)),
      },
      {
        path: 'plans/:planId/goods',
        ...withOnboardingLayout(withSuspense(GoodsList)),
      },
      {
        path: 'plans/:planId/goods/:goodsId',
        ...withOnboardingLayout(withSuspense(GoodsBuilder)),
      },
      {
        path: 'plans/:planId/goods/:goodsId/entries',
        ...withOnboardingLayout(withSuspense(GoodsViewer)),
      },
      {
        path: 'suppliers',
        element: withSuspense(SuppliersList),
      },
      {
        path: 'suppliers/:supplierId',
        element: withSuspense(SupplierViewer),
      },
      // Releases routes - list and create use regular layout
      {
        path: 'plans/:planId/releases',
        element: withSuspense(ReleasesList),
      },
      {
        path: 'plans/:planId/releases/create',
        element: withSuspense(ReleasesCreate),
      },
      // Releases viewer routes - all use AuditLayoutWrapper with 'latest' viewMode
      {
        path: 'plans/:planId/releases/:releaseId',
        ...withAuditLayout(ReleasesDetail),
      },
      {
        path: 'plans/:planId/releases/:releaseId/chapters/:chapterId',
        ...withAuditLayout(
          ReleaseChapterPages,
          <ChapterSnapshotProvider>
            <Suspense fallback={<LoadingFallback />}>
              <ReleaseChapterPages />
            </Suspense>
          </ChapterSnapshotProvider>
        ),
      },
      {
        path: 'plans/:planId/releases/:releaseId/chapters/:chapterId/pages/:pageId',
        ...withAuditLayout(
          ReleasePageContent,
          <ChapterSnapshotProvider>
            <Suspense fallback={<LoadingFallback />}>
              <ReleasePageContent />
            </Suspense>
          </ChapterSnapshotProvider>
        ),
      },
      {
        path: 'plans/:planId/releases/:releaseId/chapters/:chapterId/hazards/:hazardId',
        ...withAuditLayout(ReleaseHazardContent),
      },
      {
        path: 'plans/:planId/releases/:releaseId/chapters/:chapterId/ccps/:ccpId',
        ...withAuditLayout(ReleaseCCPContent),
      },
      {
        path: 'plans/:planId/releases/:releaseId/records',
        ...withAuditLayout(ReleaseRecordCollections),
      },
      {
        path: 'plans/:planId/releases/:releaseId/records/:collectionId',
        ...withAuditLayout(ReleaseRecordsViewer),
      },
      {
        path: 'plans/:planId/releases/:releaseId/goods',
        ...withAuditLayout(ReleaseGoodsCollections),
      },
      {
        path: 'plans/:planId/releases/:releaseId/goods/:goodsId',
        ...withAuditLayout(ReleaseGoodsViewer),
      },
      // Audits routes - all use AuditProvider with 'edit' viewMode
      {
        path: 'plans/:planId/audits',
        ...withAuditProvider(AuditsList),
      },
      {
        path: 'plans/:planId/audits/create',
        ...withAuditProvider(AuditCreate),
      },
      {
        path: 'plans/:planId/audits/:auditId',
        ...withAuditProvider(AuditViewer),
      },
      {
        path: 'plans/:planId/audits/:auditId/report',
        element: withSuspense(AuditReport),
        handle: { viewMode: 'edit' } satisfies RouteHandle,
      },
      {
        path: 'plans/:planId/corrective-actions',
        ...withAuditProvider(CorrectiveActionsList),
      },
    ],
  },

  // Short URL routes - all use MinimalAppWrapper (allows anonymous access if enabled)
  // Records short URLs (/r/...)
  {
    path: '/r',
    element: <MinimalAppWrapper />,
    children: [
      {
        path: 'plan/:planId',
        element: withSuspense(RecordPlanViewer),
      },
      {
        path: ':collectionId',
        element: withSuspense(RecordCollectorPage),
      },
    ],
  },
  // Goods short URLs (/g/...)
  {
    path: '/g',
    element: <MinimalAppWrapper />,
    children: [
      {
        path: 'plan/:planId',
        element: withSuspense(GoodsPlanViewer),
      },
      {
        path: ':goodsId',
        element: withSuspense(GoodsReceiverPage),
      },
    ],
  },

  // Onboarding routes (protected)
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <OnboardingWrapper />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'organisation',
        element: withSuspense(OnboardingOrganisation),
      },
      {
        path: 'payment',
        element: withSuspense(OnboardingPayment),
      },
      {
        path: 'subscription',
        element: withSuspense(OnboardingSubscription),
      },
      {
        path: 'welcome',
        element: withSuspense(OnboardingWelcome),
      },
    ],
  },

  // Settings sub-routes (protected)

  {
    path: 'settings',
    element: (
      <ProtectedRoute>
        <NotificationsProvider>
          <PlanProvider>
            <ReleasesProvider>
              <QueueProvider>
                <AppWrapper />
              </QueueProvider>
            </ReleasesProvider>
          </PlanProvider>
        </NotificationsProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: (
          <PermissionsRoute>
            <Suspense fallback={<LoadingFallback />}>
              <Settings />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <PermissionsRoute roles={ProfileRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Profile />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'organisation',
        element: (
          <PermissionsRoute roles={OrganisationRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Organisation />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'billing',
        element: (
          <PermissionsRoute roles={InvoiceRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Cards />
              <Invoices />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <PermissionsRoute roles={NotificationRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Notifications />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'security',
        element: (
          <PermissionsRoute roles={SecurityRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Security />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'subscription',
        element: (
          <PermissionsRoute roles={SubscriptionRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Subscription />
            </Suspense>
          </PermissionsRoute>
        ),
      },
      {
        path: 'team',
        element: (
          <PermissionsRoute roles={TeamRoles}>
            <Suspense fallback={<LoadingFallback />}>
              <Team />
            </Suspense>
          </PermissionsRoute>
        ),
      },
    ],
  },

  // Upgrade page (shown after free trial expires - standalone, no AppWrapper)
  {
    path: '/upgrade',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <UpgradePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Errors
  {
    path: '/403',
    element: withSuspense(Error403),
  },
  {
    path: '/500',
    element: withSuspense(Error500),
  },
  {
    path: '/404',
    element: withSuspense(Error404),
  },
  {
    path: '/418',
    element: withSuspense(Error418),
  },

  // 404 fallback
  {
    path: '*',
    element: withSuspense(Error404),
  },
];
